import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import type { CommunicationLog } from '../../data/types';
import { useRegistry } from '../../store/RegistryStore';
import { useTenantScope } from '../../store/selectors';
import { useToast } from '../../ui/feedback';

type Audience = 'ALL' | 'TASKS' | 'NO_TFN' | string;

export default function Composer({ onSent }: { onSent: () => void }) {
  const scope = useTenantScope();
  const { state, dispatch } = useRegistry();
  const toast = useToast();
  const [audience, setAudience] = useState<Audience>('ALL');
  const [subject, setSubject] = useState('December quarter update and holiday contact hours');
  const [message, setMessage] = useState(
    'Our team will be available from 2 January. Distributions continue as scheduled over the break, and your statements remain available in the app.',
  );
  const [channels, setChannels] = useState<CommunicationLog['channels']>(['EMAIL', 'PUSH']);

  const recipients = useMemo(() => {
    const active = new Set(scope.holdings.map((holding) => holding.holderId));
    if (audience === 'ALL') return Array.from(active);
    if (audience === 'TASKS')
      return Array.from(
        new Set(
          state.tasks
            .filter((task) => task.status === 'OPEN' && scope.holderIds.has(task.holderId))
            .map((task) => task.holderId),
        ),
      );
    if (audience === 'NO_TFN')
      return scope.holders
        .filter((holder) => holder.tfnStatus === 'NOT_QUOTED' && active.has(holder.id))
        .map((holder) => holder.id);
    return Array.from(
      new Set(
        scope.holdings
          .filter((holding) => holding.fundId === audience)
          .map((holding) => holding.holderId),
      ),
    );
  }, [audience, scope, state.tasks]);
  const sample = scope.holderById.get(recipients[0] ?? '');
  const audienceLabel =
    audience === 'ALL'
      ? 'All holders'
      : audience === 'TASKS'
        ? 'Holders with open tasks'
        : audience === 'NO_TFN'
          ? 'Holders without a TFN'
          : `${scope.fundById.get(audience)?.code} holders`;
  const toggle = (channel: CommunicationLog['channels'][number]) =>
    setChannels((current) =>
      current.includes(channel)
        ? current.filter((item) => item !== channel)
        : [...current, channel],
    );

  return (
    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' } }}>
      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Stack spacing={2}>
          <TextField
            select
            label="Audience"
            value={audience}
            onChange={(event) => setAudience(event.target.value)}
            helperText={`${recipients.length} recipients`}
          >
            <MenuItem value="ALL">All holders</MenuItem>
            <MenuItem value="TASKS">Holders with open tasks</MenuItem>
            <MenuItem value="NO_TFN">Holders without a TFN</MenuItem>
            {scope.funds
              .filter((fund) => fund.status !== 'WOUND_UP')
              .map((fund) => (
                <MenuItem key={fund.id} value={fund.id}>
                  {fund.code} holders
                </MenuItem>
              ))}
          </TextField>
          <TextField
            label="Subject"
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
          />
          <TextField
            label="Message"
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            multiline
            minRows={5}
            helperText="Merge fields: {{firstName}}, {{account}}"
          />
          <Stack direction="row">
            {(['EMAIL', 'PUSH', 'POST'] as const).map((channel) => (
              <FormControlLabel
                key={channel}
                control={
                  <Checkbox checked={channels.includes(channel)} onChange={() => toggle(channel)} />
                }
                label={
                  channel === 'PUSH'
                    ? 'App notification'
                    : channel.charAt(0) + channel.slice(1).toLowerCase()
                }
              />
            ))}
          </Stack>
          <Box>
            <Button
              variant="contained"
              disabled={!recipients.length || !subject.trim() || !channels.length}
              onClick={() => {
                dispatch({
                  type: 'COMMUNICATION_SEND',
                  log: {
                    template: 'GENERAL_UPDATE',
                    subject,
                    audience: audienceLabel,
                    fundId: scope.fundById.has(audience) ? audience : undefined,
                    holderIds: recipients,
                    channels,
                  },
                });
                toast(`Sent to ${recipients.length} holders`);
                onSent();
              }}
            >
              Send to {recipients.length} holders
            </Button>
          </Box>
        </Stack>
      </Paper>
      <Paper variant="outlined" sx={{ p: 2.5, bgcolor: '#F8FAFC' }}>
        <Typography variant="caption" color="text.secondary">
          Preview for {sample?.contactName ?? 'a holder'}
        </Typography>
        <Box
          sx={{
            bgcolor: '#fff',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            p: 2.5,
            mt: 1,
          }}
        >
          <Typography
            sx={{
              fontFamily: "'Manrope', sans-serif",
              fontWeight: 800,
              color: scope.tenant.accent,
            }}
          >
            {scope.tenant.shortName}
          </Typography>
          <Typography variant="h6" sx={{ mt: 1.5 }}>
            {subject}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1.5, whiteSpace: 'pre-line' }}>
            {`Dear ${sample?.contactName.split(' ')[0] ?? 'Investor'},\n\n${message.replace(/\{\{firstName\}\}/g, sample?.contactName.split(' ')[0] ?? '').replace(/\{\{account\}\}/g, sample?.accountNumber ?? '')}`}
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 2 }}>
            {scope.tenant.name} · Manage your notification settings in the app.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}
